import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { useParams, Link } from "react-router-dom";
import { Download, XCircle, Package2, MapPin } from "lucide-react";
import { StatusBadge } from "./Dashboard";
import { toast, Toaster } from "sonner";

export default function Tracking() {
  const { bookNumber } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await api.get(`/shipments/${bookNumber}/tracking`);
    setData(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [bookNumber]);

  const downloadLabel = async () => {
    // Prefer the EasyPost-backed PDF proxy (/api/files/label/:book_number) — opens directly
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("sp_token") || "";
      const resp = await fetch(`${apiBase}/api/files/label/${bookNumber}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `sellepack_${bookNumber}.pdf`; a.click();
        URL.revokeObjectURL(url);
        return;
      }
      // Fall back to legacy mock label endpoint
      const { data } = await api.get(`/shipments/${bookNumber}/label`);
      const bin = atob(data.content_b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: data.mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = data.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download label"); }
  };

  const voidShipment = async () => {
    if (!window.confirm("Void this shipment? Wallet will be refunded.")) return;
    try {
      await api.post(`/shipments/${bookNumber}/void`);
      toast.success("Voided & refunded"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
  };

  if (loading) return <DashboardLayout><div className="font-mono-plex text-zinc-500">Loading_</div></DashboardLayout>;
  if (!data) return <DashboardLayout><div>Not found</div></DashboardLayout>;

  const s = data.shipment;
  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-4 text-sm"><Link to="/dashboard/shipments" className="brand-text">← Back to shipments</Link></div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 sm:mb-8 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">Shipment</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter font-mono-plex break-all">{s.book_number}</h1>
          <div className="text-zinc-600 mt-1 text-xs sm:text-sm">
            {s.carrier_name} · {s.service_name} · <span className="font-mono-plex">{s.tracking_number}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/dashboard/smart-services" data-testid="add-smart-service-link"
            className="border border-[var(--brand-accent)] brand-text px-3 sm:px-4 py-2 rounded-sm text-xs sm:text-sm flex items-center gap-2 hover:bg-blue-50">
            + Smart Service
          </Link>
          <button data-testid="download-label-btn" onClick={downloadLabel} className="border border-zinc-300 px-3 sm:px-4 py-2 rounded-sm text-xs sm:text-sm flex items-center gap-2 hover:bg-zinc-50">
            <Download size={14} /> Label PDF
          </button>
          {s.status === "BOOKED" && (
            <button data-testid="void-shipment-btn" onClick={voidShipment} className="border border-red-300 text-red-700 px-3 sm:px-4 py-2 rounded-sm text-xs sm:text-sm flex items-center gap-2 hover:bg-red-50">
              <XCircle size={14} /> Void
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white border border-zinc-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="font-display text-xl font-bold">Tracking timeline</div>
            <StatusBadge status={data.current_status || s.status} />
          </div>
          <div className="space-y-5">
            {(data.events || []).map((ev, i) => (
              <div key={i} className="flex gap-4">
                <div className="relative flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full ${i === 0 ? "brand-bg" : "bg-zinc-300"}`} />
                  {i < data.events.length - 1 && <div className="w-px flex-1 bg-zinc-200 mt-1" />}
                </div>
                <div className="pb-4">
                  <div className="font-mono-plex text-xs text-zinc-500">{new Date(ev.timestamp).toLocaleString()}</div>
                  <div className="font-display font-bold text-zinc-900">{ev.status.replace(/_/g, " ")}</div>
                  <div className="text-sm text-zinc-600">{ev.location} — {ev.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-2"><MapPin size={12} /> Sender</div>
            <div className="text-sm">
              <div className="font-medium">{s.sender.name}</div>
              <div className="text-zinc-600">{s.sender.address1}</div>
              <div className="text-zinc-600">{s.sender.city}, {s.sender.state} {s.sender.zip}</div>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-2"><MapPin size={12} /> Receiver</div>
            <div className="text-sm">
              <div className="font-medium">{s.receiver.name}</div>
              <div className="text-zinc-600">{s.receiver.address1}</div>
              <div className="text-zinc-600">{s.receiver.city}, {s.receiver.state} {s.receiver.zip}</div>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-2"><Package2 size={12} /> Package & Cost</div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span>Base rate</span><span className="font-mono-plex">${s.base_rate.toFixed(2)}</span></div>
              {s.surcharges?.map((x, i) => (
                <div key={i} className="flex justify-between text-amber-700 text-xs"><span>+ {x.description}</span><span className="font-mono-plex">${x.amount.toFixed(2)}</span></div>
              ))}
              <div className="flex justify-between"><span>Markup</span><span className="font-mono-plex">${s.markup.toFixed(2)}</span></div>
              <div className="flex justify-between pt-2 border-t border-zinc-200 font-display font-black"><span>Total</span><span>${s.total.toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
